import { useMemo, useState } from "react";
import { TrendingUp, Target, CalendarClock, ShoppingBag, Info } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceDot, ReferenceLine,
} from "recharts";
import { rupiah, rupiahShort, formatNumber } from "@/lib/format";
import { computeBep, buildChartData } from "@/lib/bep";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Rupiah with sign in front: "-Rp 500.000" instead of "Rp -500.000".
const rupiahSigned = (n) => (n < 0 ? "-" + rupiah(Math.abs(n)) : rupiah(n));

// Only accept empty or non-negative values (integers only when `integer` is set).
const guard = (setter, integer = false) => (e) => {
  const v = e.target.value;
  if (v === "") return setter("");
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return;
  if (integer && !/^\d+$/.test(v)) return;
  setter(v);
};

function Note({ children, tone = "muted", testid }) {
  const tones = {
    muted: "bg-muted/60 text-muted-foreground",
    danger: "bg-destructive/10 text-destructive",
    success: "bg-primary/10 text-primary",
    warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  };
  return (
    <p className={`mt-3 flex gap-2 rounded-lg p-2.5 text-xs leading-relaxed ${tones[tone]}`} data-testid={testid}>
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function Row({ label, value, strong, testid }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-2 ${strong ? "font-semibold" : ""}`}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-mono text-sm" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Bep() {
  const [fixedCost, setFixedCost] = useState("3000000");
  const [price, setPrice] = useState("15000");
  const [variable, setVariable] = useState("6000");
  const [target, setTarget] = useState("");
  const [capital, setCapital] = useState("15000000");

  const r = useMemo(
    () => computeBep({ fixedCost, price, variable, target, capital }),
    [fixedCost, price, variable, target, capital],
  );
  const chartData = useMemo(() => buildChartData(r.inputs, r.bepUnit), [r]);
  const { P } = r.inputs;
  const tr = r.targetResult;

  const statusText = {
    rugi: "Usaha diperkirakan mengalami kerugian operasional.",
    impas: "Usaha berada di titik impas.",
    laba: "Usaha diperkirakan memperoleh laba operasional.",
  };
  const statusTone = { rugi: "danger", impas: "warning", laba: "success" };

  const gap = tr && r.bepValid ? r.inputs.T - r.bepUnit : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Kalkulator BEP (Titik Impas)</h1>
        <p className="text-sm text-muted-foreground">
          Cari tahu berapa produk harus terjual agar tidak rugi, berapa perkiraan laba dari target penjualan Anda, dan kapan modal kembali.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-5">
          {/* Section A — BEP */}
          <Card className="rounded-2xl p-5">
            <div className="mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">1. Hitung Titik Impas (BEP)</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Biaya Tetap per Bulan (Rp)</Label>
                <Input type="number" min="0" inputMode="decimal" value={fixedCost} onChange={guard(setFixedCost)}
                  placeholder="contoh: sewa, gaji, listrik" className="rounded-xl font-mono" data-testid="bep-fixed-cost-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Harga Jual per Unit (Rp)</Label>
                  <Input type="number" min="0" inputMode="decimal" value={price} onChange={guard(setPrice)}
                    className="rounded-xl font-mono" data-testid="bep-price-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Biaya Variabel per Unit (Rp)</Label>
                  <Input type="number" min="0" inputMode="decimal" value={variable} onChange={guard(setVariable)}
                    className="rounded-xl font-mono" data-testid="bep-variable-input" />
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-card p-4 shadow-sm" data-testid="bep-contrib-result">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Margin Kontribusi per Unit</p>
              <p className="font-mono text-xl font-bold">
                {r.contrib !== null ? rupiahSigned(r.contrib) : "-"}
              </p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-primary p-4 text-primary-foreground" data-testid="bep-unit-result">
                <p className="text-xs uppercase tracking-wider opacity-80">BEP Unit / bulan</p>
                <p className="font-mono text-2xl font-extrabold">
                  {r.bepValid ? `${formatNumber(r.bepUnit)} unit` : "-"}
                </p>
              </div>
              <div className="rounded-xl bg-card p-4 shadow-sm" data-testid="bep-rupiah-result">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">BEP Omzet / bulan</p>
                <p className="font-mono text-xl font-bold">{r.bepValid ? rupiah(r.bepOmzet) : "-"}</p>
              </div>
            </div>

            {!r.baseReady ? (
              <Note testid="bep-base-hint">Lengkapi biaya tetap, harga jual, dan biaya variabel untuk melihat hasil BEP.</Note>
            ) : !r.bepValid ? (
              <Note tone="danger" testid="bep-invalid-msg">
                BEP tidak dapat dihitung karena harga jual per unit sama dengan atau lebih kecil dari biaya variabel per unit,
                sehingga tidak ada margin kontribusi positif. Naikkan harga jual atau turunkan biaya variabel.
              </Note>
            ) : (
              <Note testid="bep-summary">
                Anda perlu menjual minimal <b>{formatNumber(r.bepUnit)} unit</b> per bulan (omzet {rupiah(r.bepOmzet)}) agar
                tidak rugi. Jumlah unit dibulatkan ke atas karena produk tidak bisa dijual setengah.
              </Note>
            )}
            <div className="mt-3 space-y-1.5 text-xs leading-relaxed text-muted-foreground">
              <p><b>Margin kontribusi</b> adalah sisa uang dari setiap penjualan setelah dikurangi biaya variabel.</p>
              <p><b>BEP</b> adalah titik impas, yaitu perkiraan jumlah penjualan minimum agar pendapatan dapat menutup biaya tetap dan biaya variabel.</p>
            </div>
          </Card>

          {/* Section B — Target & operating profit */}
          <Card className="rounded-2xl p-5">
            <div className="mb-4 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">2. Target Penjualan & Estimasi Laba</h3>
            </div>
            <div className="space-y-1.5">
              <Label>Target Penjualan per Bulan (Unit)</Label>
              <Input type="number" min="0" step="1" inputMode="numeric" value={target} onChange={guard(setTarget, true)}
                placeholder="contoh: 1000" className="rounded-xl font-mono" data-testid="bep-target-input" />
            </div>

            {tr ? (
              <>
                <div className="mt-3 divide-y divide-border">
                  <Row label="Target Omzet" value={rupiah(tr.omzet)} testid="bep-target-omzet" />
                  <Row label="Total Biaya Variabel" value={rupiah(tr.totalVariable)} testid="bep-target-variable" />
                  <Row label="Total Biaya (tetap + variabel)" value={rupiah(tr.totalCost)} testid="bep-target-total-cost" />
                </div>
                <div className={`mt-2 rounded-xl p-4 ${tr.profit < 0 ? "bg-destructive/10" : tr.profit > 0 ? "bg-primary/10" : "bg-amber-500/10"}`}
                  data-testid="bep-profit-result">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Estimasi Laba Operasional / bulan</p>
                  <p className={`font-mono text-2xl font-extrabold ${tr.profit < 0 ? "text-destructive" : tr.profit > 0 ? "text-primary" : "text-amber-600 dark:text-amber-400"}`}>
                    {rupiahSigned(tr.profit)}
                  </p>
                </div>
                <Note tone={statusTone[tr.status]} testid="bep-profit-status">
                  {statusText[tr.status]}
                  {gap !== null && gap !== 0 && (
                    <> Target Anda {formatNumber(Math.abs(gap))} unit {gap > 0 ? "di atas" : "di bawah"} BEP.</>
                  )}
                </Note>
              </>
            ) : (
              <Note testid="bep-target-hint">
                {r.baseReady
                  ? "Isi target penjualan (bilangan bulat) untuk melihat perkiraan omzet, biaya, dan laba per bulan."
                  : "Lengkapi data di bagian 1 dan isi target penjualan untuk melihat perkiraan laba."}
              </Note>
            )}
          </Card>

          {/* Section C — Payback */}
          <Card className="rounded-2xl p-5">
            <div className="mb-4 flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-amber-500" />
              <h3 className="font-heading text-lg font-semibold">3. Perkiraan Balik Modal</h3>
            </div>
            <div className="space-y-1.5">
              <Label>Modal Awal (Rp)</Label>
              <Input type="number" min="0" inputMode="decimal" value={capital} onChange={guard(setCapital)}
                className="rounded-xl font-mono" data-testid="bep-capital-input" />
            </div>
            <div className="mt-4 rounded-xl bg-amber-500/10 p-4 text-center" data-testid="bep-months-result">
              <p className="text-xs uppercase tracking-wider text-amber-600 dark:text-amber-400">Estimasi Balik Modal</p>
              <p className="font-mono text-3xl font-extrabold text-amber-600 dark:text-amber-400">
                {r.payback.state === "ok" ? `${formatNumber(r.payback.months)} bulan` : "-"}
              </p>
              {r.payback.state === "ok" && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {rupiah(r.inputs.C)} ÷ {rupiah(tr.profit)} per bulan, dibulatkan ke atas
                </p>
              )}
            </div>
            {r.payback.state === "ok" && (
              <Note testid="bep-payback-note">
                Ini adalah perkiraan berdasarkan estimasi laba operasional per bulan pada target penjualan yang Anda pilih,
                bukan jaminan. Hasil nyata bisa berbeda jika penjualan atau biaya berubah.
              </Note>
            )}
            {r.payback.state === "need_capital" && <Note testid="bep-payback-note">Isi modal awal untuk menghitung perkiraan balik modal.</Note>}
            {r.payback.state === "no_investment" && (
              <Note tone="success" testid="bep-payback-note">Modal awal Rp 0, jadi tidak ada masa balik modal yang perlu dihitung.</Note>
            )}
            {r.payback.state === "need_target" && (
              <Note testid="bep-payback-note">Lengkapi data BEP dan target penjualan di bagian 1 dan 2 terlebih dahulu.</Note>
            )}
            {r.payback.state === "loss" && (
              <Note tone="danger" testid="bep-payback-note">
                Balik modal belum bisa diperkirakan karena usaha diperkirakan masih rugi pada target penjualan ini.
                Coba naikkan target penjualan atau harga jual, atau kurangi biaya.
              </Note>
            )}
            {r.payback.state === "zero_profit" && (
              <Note tone="warning" testid="bep-payback-note">
                Laba operasional per bulan masih Rp 0 (titik impas), sehingga masa balik modal belum bisa dihitung.
              </Note>
            )}
          </Card>
        </div>

        <div className="lg:col-span-7">
          <Card className="rounded-2xl p-5 lg:sticky lg:top-6">
            <div className="mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Grafik Proyeksi BEP</h3>
            </div>
            {chartData.length > 0 ? (
              <>
                <div className="h-[360px] w-full" data-testid="bep-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ left: 4, right: 12, top: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                      <XAxis dataKey="unit" type="number" domain={[0, "dataMax"]} tick={{ fontSize: 11 }}
                        tickFormatter={(v) => formatNumber(v)} stroke="hsl(var(--muted-foreground))"
                        label={{ value: "Unit Terjual", position: "insideBottom", offset: -2, fontSize: 11 }} />
                      <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={64} />
                      <Tooltip formatter={(v) => rupiah(v)} labelFormatter={(l) => `${formatNumber(l)} unit`}
                        contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="linear" dataKey="pendapatan" name="Pendapatan" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={false} />
                      <Line type="linear" dataKey="biaya" name="Total Biaya" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={false} />
                      {tr && (
                        <ReferenceLine x={r.inputs.T} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4"
                          label={{ value: "Target", position: "top", fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      )}
                      {r.bepValid && (
                        <ReferenceDot x={r.bepUnit} y={r.bepUnit * P} r={6} fill="hsl(var(--chart-4))" stroke="#fff" strokeWidth={2} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  {r.bepValid
                    ? "Titik bulat = titik impas (BEP). Di kanan titik ini pendapatan lebih besar dari total biaya. Garis putus-putus = target penjualan Anda."
                    : "Garis pendapatan tidak pernah melewati total biaya karena margin kontribusi tidak positif, sehingga tidak ada titik impas."}
                </p>
              </>
            ) : (
              <div className="flex h-[360px] items-center justify-center rounded-xl bg-muted/40 p-6 text-center text-sm text-muted-foreground"
                data-testid="bep-chart-empty">
                Lengkapi biaya tetap, harga jual, dan biaya variabel untuk melihat grafik.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
